import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import { Bird, Mail, Lock, Loader2, ArrowRight, Zap, Shield, KeyRound, Sparkles, RotateCcw, Check } from "lucide-react";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";

export default function AuthPage() {
  const { setAuth } = useAppContext();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite") || "";
  const pitchMode = searchParams.get("mode") === "pitch";
  const signinMode = searchParams.get("mode") === "signin";

  const lang = localStorage.getItem("pigeon-lang") || "en";
  const { t } = useTranslation(lang);

  const [authMode, setAuthMode] = useState(pitchMode || signinMode ? "signin" : "magic");
  const [email, setEmail] = useState(pitchMode ? "pitchdemo@onepigeon.app" : "");
  const [password, setPassword] = useState(pitchMode ? "Love2pitch123*" : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState("email"); // email, token, done
  const [resetMsg, setResetMsg] = useState("");

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setError("Please enter a valid email"); return; }
    setLoading(true);
    setError("");
    try {
      const magicRes = await api.requestMagicLink(email);
      const verifyRes = await api.verifyMagicLink(magicRes.token);
      const authData = { jwt: verifyRes.jwt, user: verifyRes.user };
      setAuth(authData);
      localStorage.setItem("pigeon-auth", JSON.stringify(authData));
      nav(inviteCode ? `/join?invite=${inviteCode}` : "/portal");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    }
    setLoading(false);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Email and password required"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await api.loginWithPassword(email, password);
      const authData = { jwt: res.jwt, user: res.user };
      setAuth(authData);
      localStorage.setItem("pigeon-auth", JSON.stringify(authData));
      nav(inviteCode ? `/join?invite=${inviteCode}` : "/portal");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    }
    setLoading(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setError("Enter a valid email"); return; }
    setLoading(true); setError("");
    try {
      const res = await api.requestPasswordReset(email);
      setResetToken(res.reset_token || "");
      setResetStep("token");
      setResetMsg(res.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Request failed");
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken || newPassword.length < 6) { setError("Token and password (min 6 chars) required"); return; }
    setLoading(true); setError("");
    try {
      await api.resetPassword(resetToken, newPassword);
      setResetStep("done");
      setResetMsg("Password reset! You can now sign in.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed");
    }
    setLoading(false);
  };

  // FORGOT PASSWORD VIEW
  if (showForgot) {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="forgot-password-page">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(101,163,13,0.05),transparent_60%)]" />
        </div>
        <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav("/landing")}>
            <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
              <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
            </div>
            <span className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</span>
          </div>
          <LanguageSwitcher compact />
        </header>
        <main className="relative z-10 flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-50 border border-amber-300 rounded-xl flex items-center justify-center mx-auto mb-6">
                <RotateCcw className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-3">
                {resetStep === "done" ? "Done" : "Reset Password"}
              </h2>
            </div>

            {resetStep === "email" && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input data-testid="reset-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 outline-none" autoFocus />
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
                <button data-testid="reset-submit-btn" type="submit" disabled={loading}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Reset Link <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}

            {resetStep === "token" && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {resetMsg && <p className="text-sm text-lime-600 font-medium bg-lime-50 border border-lime-200 rounded-xl p-3">{resetMsg}</p>}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Reset Token</label>
                  <input data-testid="reset-token-input" type="text" value={resetToken} onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Paste your reset token"
                    className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-mono text-sm placeholder:text-slate-400 focus:border-lime-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input data-testid="reset-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 outline-none" autoFocus />
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm font-medium">{error}</p>}
                <button data-testid="reset-confirm-btn" type="submit" disabled={loading}
                  className="w-full py-4 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Set New Password <Check className="w-4 h-4" /></>}
                </button>
              </form>
            )}

            {resetStep === "done" && (
              <div className="text-center">
                <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-lime-500" />
                </div>
                <p className="text-sm text-slate-500 mb-6">{resetMsg}</p>
                <button data-testid="reset-back-signin" onClick={() => { setShowForgot(false); setAuthMode("signin"); setResetStep("email"); setNewPassword(""); setResetToken(""); }}
                  className="px-8 py-4 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase">
                  Sign In
                </button>
              </div>
            )}

            {resetStep !== "done" && (
              <button onClick={() => { setShowForgot(false); setError(""); }} className="w-full mt-4 text-center text-xs text-slate-400 font-bold hover:text-slate-600">
                Back to Sign In
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="auth-page">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(101,163,13,0.05),transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-white to-transparent" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav("/landing")}>
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{t("lobby_subtitle")}</p>
          </div>
        </div>
        <LanguageSwitcher compact />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-6">
              {authMode === "signin" ? <KeyRound className="w-8 h-8 text-lime-500" /> : <Shield className="w-8 h-8 text-lime-500" />}
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-3">
              {inviteCode ? t("join_hub_title") || "Join Hub" : authMode === "signin" ? t("auth_signin_title") || "Sign In" : t("auth_access_title") || "Command Access"}
            </h2>
            <p className="text-sm text-slate-400 font-medium">
              {inviteCode
                ? t("auth_invite_desc") || "Enter your email to join the hub"
                : authMode === "signin"
                ? t("auth_signin_desc") || "Log in with your email and password"
                : t("auth_magic_desc") || "Enter your email to access your supply platform"}
            </p>
          </div>

          {/* Auth Mode Toggle */}
          {!inviteCode && (
            <div className="flex bg-slate-100 rounded-xl p-1 mb-6" data-testid="auth-mode-toggle">
              <button
                data-testid="auth-mode-magic"
                onClick={() => { setAuthMode("magic"); setError(""); }}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
                  authMode === "magic" ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" /> {t("auth_tab_magic") || "Magic Link"}
              </button>
              <button
                data-testid="auth-mode-signin"
                onClick={() => { setAuthMode("signin"); setError(""); }}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
                  authMode === "signin" ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <KeyRound className="w-3.5 h-3.5" /> {t("auth_tab_signin") || "Sign In"}
              </button>
            </div>
          )}

          {inviteCode && (
            <div className="mb-6 p-3 bg-lime-50 border border-lime-300 rounded-xl flex items-center gap-3" data-testid="invite-badge">
              <Zap className="w-5 h-5 text-lime-600 shrink-0" />
              <div>
                <p className="text-xs font-bold text-lime-600 uppercase tracking-wider">Invite Code</p>
                <p className="text-sm text-slate-400 font-mono">{inviteCode}</p>
              </div>
            </div>
          )}

          {/* Magic Link Form */}
          {authMode === "magic" && (
            <form onSubmit={handleMagicLink} className="space-y-4" data-testid="magic-link-form">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("auth_email_label") || "Email Address"}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    data-testid="auth-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                    autoFocus
                  />
                </div>
              </div>
              {error && <p className="text-red-400 text-sm font-medium" data-testid="auth-error">{error}</p>}
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading || !email}
                className="w-full py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("auth_continue") || "Continue"} <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-center text-[11px] text-slate-400 font-medium">
                {t("auth_magic_note") || "No password needed. We use secure magic links."}
              </p>
            </form>
          )}

          {/* Password Sign In Form */}
          {authMode === "signin" && (
            <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="signin-form">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("auth_email_label") || "Email Address"}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    data-testid="auth-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                    autoFocus={!pitchMode}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("auth_password_label") || "Password"}</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    data-testid="auth-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                    autoFocus={pitchMode}
                  />
                </div>
              </div>
              {error && <p className="text-red-400 text-sm font-medium" data-testid="auth-error">{error}</p>}
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading || !email || !password}
                className="w-full py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("auth_signin_btn") || "Sign In"} <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-center text-[11px] text-slate-400 font-medium">
                {t("auth_signin_note") || "For existing users with a password. New users can use Magic Link."}
              </p>
              <button data-testid="forgot-password-btn" type="button" onClick={() => { setShowForgot(true); setError(""); }}
                className="w-full text-center text-xs text-red-400 font-bold hover:text-red-600 mt-2">
                {t("auth_forgot_password") || "Forgot your password?"}
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="relative z-10 text-center py-4 border-t border-slate-100">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Pigeon Supply v2.0 — Secure Access</p>
      </footer>
    </div>
  );
}
